import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AdminTabs from "./AdminTabs";
import { ChevronDown } from "./icons";

type RequestStatus = "new" | "in_review" | "offer_sent" | "cancelled";

interface RequestLineItem {
  name: string;
  quantity: number;
  price?: number;
}

interface AdminRequest {
  id: number;
  name: string;
  company: string;
  phone: string;
  email: string;
  notes: string | null;
  items: string;
  status: RequestStatus;
  has_offer: number;
  created_at: string;
}

const STATUS_LABELS: Record<RequestStatus, string> = {
  new: "В обработке",
  in_review: "На рассмотрении",
  offer_sent: "Предложение готово",
  cancelled: "Отменён клиентом",
};

export default function AdminRequestsPage() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [requests, setRequests] = useState<AdminRequest[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const loadRequests = () => {
    setIsLoadingRequests(true);
    fetch("/api/admin/requests")
      .then((res) => (res.ok ? res.json() : []))
      .then((data: AdminRequest[]) => setRequests(data))
      .catch(() => {})
      .finally(() => setIsLoadingRequests(false));
  };

  useEffect(() => {
    if (user?.role === "admin") loadRequests();
  }, [user]);

  if (!isAuthLoading && user?.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  const handleStatusChange = async (id: number, status: RequestStatus) => {
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    await fetch(`/api/admin/requests/${id}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
  };

  const handleUpload = async (id: number, file: File) => {
    setUploadingId(id);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`/api/admin/requests/${id}/offer`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        setRequests((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status: "offer_sent", has_offer: 1 } : r))
        );
      }
    } finally {
      setUploadingId(null);
    }
  };

  const parseItems = (items: string): RequestLineItem[] => {
    try {
      const parsed = JSON.parse(items) as unknown;
      return Array.isArray(parsed) ? (parsed as RequestLineItem[]) : [];
    } catch {
      return [];
    }
  };

  return (
    <section className="bg-white pb-24 pt-40 md:pb-32">
      <div className="container-industrial">
        <AdminTabs />
        <h2 className="font-sans text-xl font-semibold tracking-tight text-ink">
          Запросы ({requests.length})
        </h2>

        {isLoadingRequests ? (
          <p className="mt-6 text-sm text-steel-500">Загрузка…</p>
        ) : (
          <div className="mt-6 flex flex-col gap-3">
            {requests.map((request) => {
              const items = parseItems(request.items);
              const isExpanded = expandedId === request.id;
              return (
                <div key={request.id} className="border border-steel-200 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide2 text-steel-500">
                        {new Date(`${request.created_at.replace(" ", "T")}Z`).toLocaleDateString("ru-RU")} · {items.length} позиций
                      </p>
                      <p className="font-sans text-base font-semibold text-ink">
                        {request.company} — {request.name}
                      </p>
                      <p className="text-xs text-steel-500">
                        {request.phone} · {request.email}
                        {request.notes ? ` · ${request.notes}` : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
                      <select
                        value={request.status}
                        onChange={(e) => handleStatusChange(request.id, e.target.value as RequestStatus)}
                        className="border border-steel-300 px-3 py-2 text-sm text-ink outline-none focus:border-ink"
                      >
                        {(Object.keys(STATUS_LABELS) as RequestStatus[]).map((status) => (
                          <option key={status} value={status}>
                            {STATUS_LABELS[status]}
                          </option>
                        ))}
                      </select>
                      <label className="btn-dark-outline cursor-pointer text-center">
                        {uploadingId === request.id ? "…" : request.has_offer ? "Заменить файл" : "Загрузить файл"}
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleUpload(request.id, file);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-label="Состав заказа"
                        onClick={() => setExpandedId(isExpanded ? null : request.id)}
                        className="flex h-9 w-9 shrink-0 items-center justify-center text-steel-500 transition-colors duration-150 ease-engineered hover:text-accent"
                      >
                        <ChevronDown
                          size={18}
                          className={`transition-transform duration-200 ease-engineered ${isExpanded ? "rotate-180" : ""}`}
                        />
                      </button>
                    </div>
                  </div>
                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-engineered ${
                      isExpanded ? "mt-4 grid-rows-[1fr] border-t border-steel-100" : "grid-rows-[0fr] border-t-0"
                    }`}
                  >
                    <ul className="flex flex-col gap-1.5 overflow-hidden pt-4">
                      {items.map((item, i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between gap-4 text-sm text-steel-600"
                        >
                          <span>
                            {item.name} × {item.quantity}
                          </span>
                          {item.price !== undefined && (
                            <span className="shrink-0 text-ink">
                              {(item.price * item.quantity).toLocaleString("ru-RU")} ₸
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
